/**
 * Free gifts engine for the cart discount progress bar.
 *
 * Ported from the 310-Wonder theme (`cart-free-gifts-310.js`). Keeps the cart in
 * sync with the milestones configured in Theme settings > Cart discount progress bar:
 *
 *   - cart total >= threshold and gift missing   -> add the gift (qty 1) tagged with `_cart_free_gift`
 *   - cart total <  threshold and gift present   -> remove the gift
 *   - gift present with quantity > 1             -> force the quantity back to 1
 *   - tagged gift line no longer configured      -> remove it (orphan cleanup)
 *
 * Price basis is `total_price` (after discounts). It must stay identical to the
 * basis used in snippets/discount-progress-bar.liquid, otherwise the bar and the
 * cart contents disagree.
 *
 * The config JSON and the <cart-free-gifts> element are rendered by
 * snippets/cart-free-gifts-config.liquid (included from layout/theme.liquid).
 * Cart state is always read from /cart.js because not every `cartUpdate`
 * publisher in this theme ships cart data with the event.
 */
class CartFreeGifts extends HTMLElement {
  static SOURCE = "cart-free-gifts";
  static MAX_PASSES = 3;
  static REQUEST_TIMEOUT_MS = 10000;

  connectedCallback() {
    if (window.Shopify?.designMode) return;
    if (typeof subscribe !== "function" || typeof publish !== "function" || typeof PUB_SUB_EVENTS === "undefined") {
      console.warn("[cart-free-gifts] pubsub not available, engine disabled");
      return;
    }

    const configEl = document.querySelector("[data-cart-free-gifts-config]");
    if (!configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error("[cart-free-gifts] Invalid config JSON", error);
      return;
    }

    this.giftProperty = this.config.giftProperty || "_cart_free_gift";
    this.priceBasis = this.config.priceBasis || "total_price";
    this.gifts = (this.config.gifts || [])
      .map((gift) => ({
        threshold: Number(gift.threshold),
        variantId: Number(gift.variantId),
      }))
      .filter((gift) => gift.variantId > 0 && gift.threshold > 0);

    this.inFlight = false;
    this.rerunRequested = false;

    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === CartFreeGifts.SOURCE) return;
      this.sync();
    });

    // Reconcile the state the page was loaded with (other tab, back navigation
    // from checkout, milestone/gift changed in theme settings).
    if (Number(this.config.cartItemCount) > 0) this.sync();

    // A bfcache restore (e.g. back from checkout) re-uses the already connected
    // element, so connectedCallback does not run again and the cart it was
    // rendered with may be stale.
    this.onPageShow = (event) => {
      if (event.persisted) this.sync();
    };
    window.addEventListener("pageshow", this.onPageShow);
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.onPageShow) window.removeEventListener("pageshow", this.onPageShow);
  }

  /**
   * Fetch cart -> compute changes -> apply -> repeat until stable (bounded),
   * then refresh the cart UI once. Concurrent calls are coalesced: a call that
   * arrives mid-flight schedules exactly one extra run after the current one.
   */
  /**
   * Does the cart, as given, call for a gift to be added or removed?
   * Lets a caller that is about to re-render the cart find out first, so it can
   * hand over to sync() and show one final state instead of an interim one.
   * @param {object} cart - a /cart.js-shaped object (a change.js response works)
   */
  needsSync(cart) {
    const { additions, updates } = this.diff(cart);
    return additions.length > 0 || Object.keys(updates).length > 0;
  }

  /**
   * @param {object} [initialCart] - cart state the caller already holds, used
   *   for the first pass instead of fetching it again.
   * @returns {Promise<void>} resolves once the cart has been reconciled and the
   *   UI refreshed. A call that arrives mid-flight resolves with the run
   *   already going, and that run repeats itself afterwards.
   */
  sync(initialCart) {
    if (this.inFlight) {
      this.rerunRequested = true;
      return this.inFlightPromise || Promise.resolve();
    }
    this.inFlightPromise = this.runSync(initialCart).finally(() => {
      this.inFlightPromise = null;
    });
    return this.inFlightPromise;
  }

  async runSync(initialCart) {
    this.inFlight = true;
    let changed = false;
    // Gifts that could not be added in this run; retrying them would only repeat
    // the same failure once per pass.
    const failedVariantIds = new Set();
    // The cart surfaces as rendered by the last change this run made. Asking
    // for them with the change itself spares a separate request for the redraw,
    // and update.js answers with the whole cart as well, so checking its result
    // needs no /cart.js either. A quantity change that crossed a milestone kept
    // the line's prices waiting on four requests in a row, now two or three.
    const sections = this.sectionIds();
    const renderedWith = sections.length > 0
      ? { sections, sections_url: window.location.pathname }
      : {};
    let rendered = null;
    let nextCart = null;

    try {
      for (let pass = 0; pass < CartFreeGifts.MAX_PASSES; pass++) {
        const cart =
          pass === 0 && initialCart ? initialCart : nextCart || (await this.fetchCart());
        nextCart = null;
        const { additions, updates } = this.diff(cart);
        const pendingAdditions = additions.filter((item) => !failedVariantIds.has(item.id));
        const hasUpdates = Object.keys(updates).length > 0;
        if (pendingAdditions.length === 0 && !hasUpdates) break;

        if (!changed) this.setCheckoutLocked(true);
        changed = true;

        if (pendingAdditions.length > 0) {
          try {
            const added = await this.post("cart/add.js", {
              items: pendingAdditions,
              ...renderedWith,
            });
            rendered = added.sections || null;
          } catch (error) {
            // e.g. gift went out of stock between render and add. Don't retry it
            // in this run; the removals below must still run.
            console.error("[cart-free-gifts] add failed", error);
            pendingAdditions.forEach((item) => failedVariantIds.add(item.id));
          }
        }
        if (hasUpdates) {
          nextCart = await this.post("cart/update.js", { updates, ...renderedWith });
          rendered = nextCart.sections || null;
        }
      }

      if (changed) {
        if (!this.renderSections(rendered)) await this.refreshSections();
        publish(PUB_SUB_EVENTS.cartUpdate, { source: CartFreeGifts.SOURCE });
      }
    } catch (error) {
      console.error("[cart-free-gifts]", error);
    } finally {
      if (changed) this.setCheckoutLocked(false);
      this.inFlight = false;
      if (this.rerunRequested) {
        this.rerunRequested = false;
        this.sync();
      }
    }
  }

  /**
   * Compute the add / update payloads for the current cart state.
   * @param {object} cart - /cart.js response
   * @returns {{ additions: Array<object>, updates: Record<string, number> }}
   */
  diff(cart) {
    const total = Number(cart?.[this.priceBasis] ?? cart?.total_price) || 0;
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const additions = [];
    const updates = {};
    const configuredVariantIds = new Set(this.gifts.map((gift) => gift.variantId));

    for (const gift of this.gifts) {
      // Only lines this engine created count as "the gift is present"; an untagged
      // line of the same variant is cleaned up by the orphan loop below.
      const lines = items.filter(
        (item) => item.variant_id === gift.variantId && this.isTaggedGift(item),
      );
      const quantity = lines.reduce((sum, item) => sum + item.quantity, 0);
      const unlocked = total >= gift.threshold;

      if (unlocked && quantity === 0) {
        additions.push({
          id: gift.variantId,
          quantity: 1,
          properties: { [this.giftProperty]: "true" },
        });
      } else if (!unlocked && quantity > 0) {
        lines.forEach((line) => {
          updates[line.key] = 0;
        });
      } else if (unlocked && quantity > 1) {
        // Keep one line at quantity 1 and drop any extras.
        lines.forEach((line, index) => {
          updates[line.key] = index === 0 ? 1 : 0;
        });
      }
    }

    // Tagged lines for a milestone that no longer exists / changed product, plus
    // untagged lines of a gift variant (gift products are not meant to be bought).
    for (const item of items) {
      const isConfigured = configuredVariantIds.has(item.variant_id);
      if (this.isTaggedGift(item) ? !isConfigured : isConfigured) {
        updates[item.key] = 0;
      }
    }

    return { additions, updates };
  }

  /** A cart line this engine added (line-item property `_cart_free_gift`). */
  isTaggedGift(item) {
    return item?.properties?.[this.giftProperty] === "true";
  }

  /**
   * fetch() that gives up instead of hanging forever. A stalled cart request
   * would otherwise leave `inFlight` true and keep checkout disabled for the
   * rest of the page session. The abort frees the connection; the race also
   * covers a third-party wrapper around window.fetch that ignores the signal.
   */
  async request(url, options = {}) {
    const controller = new AbortController();
    try {
      return await this.withTimeout(fetch(url, { ...options, signal: controller.signal }));
    } catch (error) {
      controller.abort();
      throw error;
    }
  }

  /** Reject after REQUEST_TIMEOUT_MS if `promise` has not settled by then. */
  withTimeout(promise) {
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(
        () => reject(new Error("timed out")),
        CartFreeGifts.REQUEST_TIMEOUT_MS,
      );
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
  }

  async fetchCart() {
    const response = await this.request(`${window.Shopify.routes.root}cart.js`, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
    return response.json();
  }

  async post(path, body) {
    const response = await this.request(`${window.Shopify.routes.root}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || `${path} responded ${response.status}`);
    }
    return response.json();
  }

  /** The sections refreshSections() redraws, to request along with a change. */
  sectionIds() {
    const ids = [];
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && typeof cartDrawer.renderContents === "function") {
      ids.push("cart-drawer", "cart-icon-bubble");
    }
    const cartItemsEl = document.getElementById("main-cart-items");
    const cartFooterEl = document.getElementById("main-cart-footer");
    if (cartItemsEl?.dataset.id) ids.push(cartItemsEl.dataset.id);
    if (cartFooterEl?.dataset.id) ids.push(cartFooterEl.dataset.id);
    if (cartItemsEl || cartFooterEl) ids.push("cart-icon-bubble");
    return [...new Set(ids)];
  }

  /**
   * Redraw every cart surface from sections a cart request already returned.
   * @returns {boolean} false when any of them is missing or failed to draw, so
   *   the caller can fetch them instead.
   */
  renderSections(sections) {
    const ids = this.sectionIds();
    if (ids.length === 0) return true;
    if (!sections || !ids.every((id) => typeof sections[id] === "string")) return false;

    try {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.renderContents === "function") {
        cartDrawer.renderContents({ sections }, false, { current: true });
      }
      const cartItemsEl = document.getElementById("main-cart-items");
      const cartFooterEl = document.getElementById("main-cart-footer");
      if (cartItemsEl || cartFooterEl) {
        this.applyCartPageSections(sections, cartItemsEl, cartFooterEl);
      }
      return true;
    } catch (error) {
      console.error("[cart-free-gifts] render from the cart response failed", error);
      return false;
    }
  }

  /**
   * Re-render the cart drawer (when present) and the cart page sections.
   * Same approach as assets/cart-service-checkbox.js so both stay consistent.
   */
  async refreshSections() {
    const tasks = [];

    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && typeof cartDrawer.refreshCartDrawer === "function") {
      // The drawer uses its own fetch, so it is not covered by request(); cap it
      // here and swallow the failure so a hung refresh cannot wedge the sync.
      tasks.push(
        this.withTimeout(Promise.resolve(cartDrawer.refreshCartDrawer())).catch((error) => {
          console.error("[cart-free-gifts] cart drawer refresh failed", error);
        }),
      );
    }

    const cartItemsEl = document.getElementById("main-cart-items");
    const cartFooterEl = document.getElementById("main-cart-footer");
    if (cartItemsEl || cartFooterEl) {
      tasks.push(this.refreshCartPage(cartItemsEl, cartFooterEl));
    }

    await Promise.all(tasks);
  }

  async refreshCartPage(cartItemsEl, cartFooterEl) {
    const sectionIds = [];
    if (cartItemsEl?.dataset.id) sectionIds.push(cartItemsEl.dataset.id);
    if (cartFooterEl?.dataset.id) sectionIds.push(cartFooterEl.dataset.id);
    sectionIds.push("cart-icon-bubble");

    const response = await this.request(
      `${window.Shopify.routes.root}?sections=${sectionIds.join(",")}`,
    );
    if (!response.ok) throw new Error(`sections responded ${response.status}`);
    this.applyCartPageSections(await response.json(), cartItemsEl, cartFooterEl);
  }

  applyCartPageSections(data, cartItemsEl, cartFooterEl) {
    const targets = [
      { key: cartItemsEl?.dataset.id, el: cartItemsEl, selector: ".js-contents" },
      { key: cartFooterEl?.dataset.id, el: cartFooterEl, selector: ".js-contents" },
      {
        key: "cart-icon-bubble",
        // This theme renders the section straight into the header link, there is
        // no wrapping .shopify-section element to replace inside it.
        el: document.getElementById("cart-icon-bubble"),
        selector: ".shopify-section",
        replaceSelf: true,
      },
    ];

    targets.forEach((target) => {
      if (!target.key || !target.el || !data[target.key]) return;
      const parsed = new DOMParser().parseFromString(data[target.key], "text/html");
      const incoming = parsed.querySelector(target.selector);
      if (!incoming) return;
      if (target.replaceSelf) {
        target.el.innerHTML = incoming.innerHTML;
        return;
      }
      const existing = target.el.querySelector(target.selector);
      if (existing) existing.innerHTML = incoming.innerHTML;
    });

    window.cartQuantityIntent?.restore();
  }

  /**
   * Block checkout while gifts are being changed (mirrors 310). Buttons replaced
   * by a section re-render simply come back in their normal state.
   */
  setCheckoutLocked(locked) {
    const buttons = document.querySelectorAll(
      'cart-drawer button[name="checkout"], #main-cart-footer button[name="checkout"]',
    );

    buttons.forEach((button) => {
      if (locked) {
        if (button.dataset.freeGiftsLocked === "true") return;
        button.dataset.freeGiftsLocked = "true";
        button.dataset.freeGiftsWasDisabled = button.disabled ? "true" : "false";
        button.dataset.freeGiftsOriginalHtml = button.innerHTML;
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
        if (this.config.updatingText) button.textContent = this.config.updatingText;
      } else {
        if (button.dataset.freeGiftsLocked !== "true") return;
        button.innerHTML = button.dataset.freeGiftsOriginalHtml ?? button.innerHTML;
        button.disabled = button.dataset.freeGiftsWasDisabled === "true";
        button.removeAttribute("aria-busy");
        delete button.dataset.freeGiftsLocked;
        delete button.dataset.freeGiftsWasDisabled;
        delete button.dataset.freeGiftsOriginalHtml;
      }
    });
  }
}

if (!customElements.get("cart-free-gifts")) {
  customElements.define("cart-free-gifts", CartFreeGifts);
}
